/**
 * SanPlumb — Tabla de tipologías de baño (CLAUDE.md §XI)
 *
 * Cada tipología describe un patrón típico del mercado peruano: dimensiones
 * del recinto, aparatos esperados y un layout sugerido. La generación 3D del
 * baño parte de aquí en lugar de un DEMO fijo.
 *
 * Detección automática: dado un `CountsState` del Hunter, `detectTipologia()`
 * elige la tipología §XI que mejor encaja. El usuario siempre puede
 * sobreescribir con el selector.
 */

import type { CountsState } from '../types/ui';
import { APARATOS_UI } from './aparatosUI';

export type TipologiaCode =
  | 'A1'
  | 'A2'
  | 'A3'
  | 'B1'
  | 'B2'
  | 'C1'
  | 'C2'
  | 'SH'
  | 'ACC';

export interface TipologiaSpec {
  code: TipologiaCode;
  /** Nombre corto (ej. "B1 · Estándar PE"). */
  label: string;
  /** Descripción larga (subtítulo en el selector). */
  desc: string;
  /** Dimensiones del recinto en metros (§XI). */
  room: { w: number; d: number; h: number };
  /** Lista de ids de aparato (de aparatosUI) que típicamente lleva. */
  expects: readonly string[];
}

/**
 * Tabla §XI completa. `room.h` = 2.40 m (altura libre estándar peruana).
 * `room.w` × `room.d` viene directo de la tabla del baseline.
 */
export const TIPOLOGIAS: readonly TipologiaSpec[] = [
  {
    code: 'A1',
    label: 'A1 · Baño mínimo',
    desc: '2.0 m² — Lavatorio + Inodoro (sin ducha)',
    room: { w: 1.7, d: 1.2, h: 2.4 },
    expects: ['lavatorio', 'inodoro_tanque'],
  },
  {
    code: 'A2',
    label: 'A2 · Baño estrecho',
    desc: '2.2 m² — Lavatorio + Inodoro + Ducha (lineal)',
    room: { w: 2.4, d: 0.9, h: 2.4 },
    expects: ['lavatorio', 'inodoro_tanque', 'ducha'],
  },
  {
    code: 'A3',
    label: 'A3 · Baño compacto',
    desc: '2.25 m² — Lavatorio + Inodoro + Ducha (esquina)',
    room: { w: 1.5, d: 1.5, h: 2.4 },
    expects: ['lavatorio', 'inodoro_tanque', 'ducha'],
  },
  {
    code: 'B1',
    label: 'B1 · Estándar peruano',
    desc: '2.9 m² — Lavatorio + Inodoro + Ducha',
    room: { w: 1.7, d: 1.7, h: 2.4 },
    expects: ['lavatorio', 'inodoro_tanque', 'ducha'],
  },
  {
    code: 'B2',
    label: 'B2 · Con tina',
    desc: '3.2 m² — Lavatorio + Inodoro + Tina',
    room: { w: 1.8, d: 1.8, h: 2.4 },
    expects: ['lavatorio', 'inodoro_tanque', 'tina'],
  },
  {
    code: 'C1',
    label: 'C1 · Completo',
    desc: '3.6 m² — Lavatorio + Inodoro + Ducha (alargado)',
    room: { w: 2.4, d: 1.5, h: 2.4 },
    expects: ['lavatorio', 'inodoro_tanque', 'ducha'],
  },
  {
    code: 'C2',
    label: 'C2 · Completo con tina',
    desc: '3.8 m² — Lavatorio + Inodoro + Tina + Ducha',
    room: { w: 2.4, d: 1.6, h: 2.4 },
    expects: ['lavatorio', 'inodoro_tanque', 'tina', 'ducha'],
  },
  {
    code: 'SH',
    label: 'SH · Servicio',
    desc: '≈2.5 m² — Lavatorio + Inodoro + Lavadero de ropa',
    room: { w: 2.4, d: 1.1, h: 2.4 },
    expects: ['lavatorio', 'inodoro_tanque', 'lavadero'],
  },
  {
    code: 'ACC',
    label: 'ACC · Accesible',
    desc: '14.5 m² — 2 Lavatorios + Inodoro accesible + Ducha (radio giro 1.50 m)',
    room: { w: 4.45, d: 3.71, h: 2.4 },
    expects: ['lavatorio', 'inodoro_tanque', 'ducha'],
  },
] as const;

/** Lookup por code. */
const BY_CODE = new Map<TipologiaCode, TipologiaSpec>(TIPOLOGIAS.map((t) => [t.code, t]));

export function tipologiaByCode(code: TipologiaCode): TipologiaSpec {
  const t = BY_CODE.get(code);
  if (!t) throw new Error(`Tipología desconocida: ${code}`);
  return t;
}

/** Tipología por defecto cuando no hay aparatos declarados. */
export const DEFAULT_TIPOLOGIA: TipologiaCode = 'B1';

/**
 * Detecta la tipología §XI que mejor encaja con el `counts` del Hunter.
 *
 * Heurística (los IDs vienen de aparatosUI.ts):
 *   - 0 aparatos                                              → B1 (default)
 *   - inodoros + lavatorios sin ducha/tina + lavadero          → SH (servicio)
 *   - inodoros + lavatorios sin ducha/tina                     → A1 (mínimo)
 *   - + ducha + tina                                           → C2
 *   - + tina (sin ducha)                                       → B2
 *   - + ducha (sin tina)                                       → B1 / C1 / ACC
 *       * 2+ lavatorios                                        → ACC
 *       * resto                                                → B1
 */
export function detectTipologia(counts: CountsState): TipologiaCode {
  const inodoros =
    (counts['inodoro_tanque'] ?? 0) +
    (counts['inodoro_red'] ?? 0) +
    (counts['inodoro_valv'] ?? 0);
  const lavatorios = counts['lavatorio'] ?? 0;
  const duchas = counts['ducha'] ?? 0;
  const tinas = counts['tina'] ?? 0;
  const lavaderos = counts['lavadero'] ?? 0;

  const total = inodoros + lavatorios + duchas + tinas + lavaderos;
  if (total === 0) return DEFAULT_TIPOLOGIA;

  if (duchas === 0 && tinas === 0) {
    if (lavaderos > 0) return 'SH';
    return 'A1';
  }

  if (tinas > 0 && duchas > 0) return 'C2';
  if (tinas > 0) return 'B2';

  // duchas > 0
  if (lavatorios >= 2) return 'ACC';
  return 'B1';
}

/**
 * Construye los `CountsState` por defecto de una tipología — usado para
 * sincronizar el Hunter cuando el usuario cambia la tipología desde el
 * selector. Empieza con todos los ids del catálogo en 0 y suma 1 por cada
 * aparato esperado en la tipología (`expects`). El usuario puede ajustar
 * los counts con los +/- después.
 *
 * Casos especiales (anotados en §XI):
 *   - ACC (Accesible) lleva 2 lavatorios, no 1.
 */
export function countsForTipologia(code: TipologiaCode): CountsState {
  const tipo = tipologiaByCode(code);
  const counts: CountsState = {};
  for (const a of APARATOS_UI) counts[a.id] = 0;
  for (const id of tipo.expects) counts[id] = (counts[id] ?? 0) + 1;
  if (code === 'ACC') counts['lavatorio'] = 2;
  return counts;
}
